import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Edit, CheckCircle, Send, XCircle, Trash2, Clock,
  AlertTriangle, Calendar, FileText, Download, ArrowRightCircle, ThumbsUp, ThumbsDown,
} from 'lucide-react'
import {
  getEstimate, approveEstimate, sendEstimate, revertEstimate,
  acceptEstimate, declineEstimate, convertEstimateToInvoice, deleteEstimate,
  downloadEstimatePdf,
} from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import Tooltip from '../components/ui/Tooltip'
import { useUserMode } from '../contexts/UserModeContext'

/* ── Confirmation Dialog ─────────────────────────────────────────── */
function ConfirmDialog({ open, title, description, confirmLabel, confirmClass, onConfirm, onCancel, children }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 space-y-4 animate-in fade-in">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
          <button type="button" onClick={onConfirm} className={confirmClass || 'btn-primary'}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  )
}

/* ── Timeline ────────────────────────────────────────────────────── */
function EstimateTimeline({ estimate }) {
  const events = []

  if (estimate.createdAt) {
    events.push({
      label: 'Created',
      date: estimate.createdAt,
      by: estimate.createdBy?.name,
      icon: FileText,
      color: 'text-gray-400 bg-gray-100',
    })
  }
  if (estimate.approvedAt) {
    events.push({
      label: 'Approved',
      date: estimate.approvedAt,
      by: estimate.approvedBy?.name,
      icon: CheckCircle,
      color: 'text-blue-500 bg-blue-50',
    })
  }
  if (['SENT', 'ACCEPTED', 'DECLINED'].includes(estimate.status)) {
    events.push({
      label: 'Sent',
      date: estimate.sentAt || estimate.approvedAt,
      icon: Send,
      color: 'text-indigo-500 bg-indigo-50',
    })
  }
  if (estimate.status === 'ACCEPTED') {
    events.push({
      label: 'Accepted',
      date: estimate.acceptedAt || estimate.updatedAt,
      icon: ThumbsUp,
      color: 'text-emerald-500 bg-emerald-50',
    })
  }
  if (estimate.status === 'DECLINED') {
    events.push({
      label: 'Declined',
      date: estimate.declinedAt || estimate.updatedAt,
      icon: ThumbsDown,
      color: 'text-red-500 bg-red-50',
    })
  }
  if (estimate.status === 'EXPIRED') {
    events.push({
      label: 'Expired',
      date: estimate.validUntil || estimate.updatedAt,
      icon: AlertTriangle,
      color: 'text-gray-500 bg-gray-100',
    })
  }

  // Sort chronologically
  events.sort((a, b) => new Date(a.date) - new Date(b.date))

  if (events.length === 0) return null

  return (
    <div className="card">
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Timeline</h2>
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />
        <div className="space-y-4">
          {events.map((ev, i) => {
            const Icon = ev.icon
            return (
              <div key={i} className="relative flex items-start gap-3 pl-1">
                <div className={`relative z-10 flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${ev.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{ev.label}</span>
                    {ev.detail && <span className="text-sm font-semibold text-emerald-700">{ev.detail}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span>{formatDate(ev.date)}</span>
                    {ev.by && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span>{ev.by}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function EstimateDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAccountant } = useUserMode()
  const [estimate, setEstimate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState('')

  // Confirmation dialog state
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', confirmLabel: '', confirmClass: '', onConfirm: () => {}, extra: null })
  const [declineReason, setDeclineReason] = useState('')
  const declineReasonRef = useRef('')

  const load = () => {
    setLoading(true)
    getEstimate(id)
      .then((res) => setEstimate(res.data))
      .catch(() => navigate('/estimates'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [id])

  const action = async (fn, ...args) => {
    setActionLoading(fn.name || 'loading')
    try {
      const res = await fn(...args)
      if (res?.data?.driveWarning) alert(res.data.driveWarning)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally {
      setActionLoading('')
    }
  }

  const openConfirm = (opts) => setConfirm({ open: true, ...opts })
  const closeConfirm = () => { setConfirm((c) => ({ ...c, open: false })); setDeclineReason(''); declineReasonRef.current = '' }

  /* ── Loading skeleton ────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-40 bg-gray-100 rounded-xl" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    )
  }

  if (!estimate) return null

  const est = estimate
  const isDraft = est.status === 'DRAFT'
  const canSend = ['APPROVED', 'SENT'].includes(est.status)
  const totalTax = Number(est.cgst || 0) + Number(est.sgst || 0) + Number(est.igst || 0)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* ── Back + Title ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <Link to="/estimates" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">{est.number || 'DRAFT'}</h1>
      </div>

      {/* ── Converted to Invoice Banner ──────────────────────────── */}
      {est.convertedInvoiceId && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-3 text-sm">
          <ArrowRightCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          <span className="text-emerald-800 font-medium">Converted to Invoice</span>
          <Link to={`/invoices/${est.convertedInvoiceId}`} className="text-emerald-600 hover:text-emerald-800 underline">
            View Invoice
          </Link>
        </div>
      )}

      {/* ── Summary Card ─────────────────────────────────────────── */}
      <div className="card bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          {/* Left: Amount + status */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <StatusBadge status={est.status} size="lg" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900 tracking-tight">{formatCurrency(est.total, est.currency)}</p>
            </div>
          </div>

          {/* Right: Customer + dates */}
          <div className="flex flex-col items-start md:items-end gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Avatar name={est.customer?.name} size="md" />
              <div className="text-right">
                <p className="font-medium text-gray-900">{est.billToName || est.customer?.name || 'No customer'}</p>
                {est.billToAddress && <p className="text-xs text-gray-500 whitespace-pre-line">{est.billToAddress}</p>}
                {!est.billToAddress && est.customer?.email && <p className="text-xs text-gray-500">{est.customer.email}</p>}
              </div>
            </div>
            <div className="flex items-center gap-4 text-gray-500">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatDate(est.date)}</span>
              </div>
              {est.validUntil && (
                <>
                  <span className="text-gray-300">valid until</span>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{formatDate(est.validUntil)}</span>
                  </div>
                </>
              )}
            </div>
            {isAccountant && est.placeOfSupply && (
              <p className="text-xs text-gray-400">Place of Supply: {est.placeOfSupply}</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Actions Bar ──────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {!isDraft && (
          <button
            onClick={async () => {
              setActionLoading('pdf')
              try {
                const res = await downloadEstimatePdf(id)
                const url = window.URL.createObjectURL(res.data)
                const a = document.createElement('a')
                a.href = url
                a.download = `${est.number}.pdf`
                a.click()
                window.URL.revokeObjectURL(url)
              } catch (err) {
                alert(err.response?.data?.error || 'Failed to generate PDF')
              } finally {
                setActionLoading('')
              }
            }}
            disabled={!!actionLoading}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Download className="h-4 w-4" /> {actionLoading === 'pdf' ? 'Generating...' : 'Download PDF'}
          </button>
        )}
        {isDraft && (
          <>
            <Link to={`/estimates/${id}/edit`} className="btn-secondary flex items-center gap-1.5">
              <Edit className="h-4 w-4" /> Edit
            </Link>
            <button
              onClick={() => openConfirm({
                title: 'Approve Estimate',
                description: 'Approving this estimate locks it for editing. The estimate number will be assigned and it will be ready to send to the customer. This action cannot be undone.',
                confirmLabel: 'Approve',
                onConfirm: () => { closeConfirm(); action(approveEstimate, id) },
              })}
              disabled={!!actionLoading}
              className="btn-primary flex items-center gap-1.5"
            >
              <CheckCircle className="h-4 w-4" /> Approve
            </button>
            <button
              onClick={() => openConfirm({
                title: 'Delete Draft',
                description: 'This will permanently delete this draft estimate and all its line items. This cannot be undone.',
                confirmLabel: 'Delete',
                confirmClass: 'btn-danger',
                onConfirm: () => { closeConfirm(); action(deleteEstimate, id).then(() => navigate('/estimates')) },
              })}
              disabled={!!actionLoading}
              className="btn-danger flex items-center gap-1.5"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </>
        )}
        {est.status === 'APPROVED' && (
          <button
            onClick={() => openConfirm({
              title: 'Revert to Draft',
              description: 'This will revert the estimate back to draft status. The estimate number will be released and can be reassigned. Use this if you need to make changes before sending.',
              confirmLabel: 'Revert to Draft',
              confirmClass: 'btn-secondary',
              onConfirm: async () => { await action(revertEstimate, id); closeConfirm() },
            })}
            disabled={!!actionLoading}
            className="btn-ghost flex items-center gap-1.5 text-amber-600 hover:text-amber-700"
          >
            <Edit className="h-4 w-4" /> Revert to Draft
          </button>
        )}
        {canSend && est.status !== 'SENT' && (
          <button
            onClick={() => action(sendEstimate, id)}
            disabled={!!actionLoading}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Send className="h-4 w-4" /> {actionLoading === 'loading' ? 'Sending...' : 'Mark as Sent'}
          </button>
        )}
        {['APPROVED', 'SENT'].includes(est.status) && (
          <>
            <button
              onClick={() => openConfirm({
                title: 'Accept Estimate',
                description: 'Mark this estimate as accepted by the customer. You can then convert it to an invoice.',
                confirmLabel: 'Accept',
                onConfirm: () => { closeConfirm(); action(acceptEstimate, id) },
              })}
              disabled={!!actionLoading}
              className="btn-primary flex items-center gap-1.5"
            >
              <ThumbsUp className="h-4 w-4" /> Accept
            </button>
            <button
              onClick={() => {
                setDeclineReason('')
                declineReasonRef.current = ''
                openConfirm({
                  title: 'Decline Estimate',
                  description: 'Mark this estimate as declined by the customer.',
                  confirmLabel: 'Decline',
                  confirmClass: 'btn-danger',
                  onConfirm: () => {
                    const reason = declineReasonRef.current
                    closeConfirm()
                    action(declineEstimate, id, reason || undefined)
                  },
                  extra: 'declineReason',
                })
              }}
              disabled={!!actionLoading}
              className="btn-danger flex items-center gap-1.5"
            >
              <ThumbsDown className="h-4 w-4" /> Decline
            </button>
          </>
        )}
        {est.status === 'ACCEPTED' && !est.convertedInvoiceId && (
          <button
            onClick={async () => {
              setActionLoading('convert')
              try {
                const res = await convertEstimateToInvoice(id)
                navigate(`/invoices/${res.data.id}`)
              } catch (err) {
                alert(err.response?.data?.error || 'Failed to convert to invoice')
              } finally {
                setActionLoading('')
              }
            }}
            disabled={!!actionLoading}
            className="btn-primary flex items-center gap-1.5"
          >
            <ArrowRightCircle className="h-4 w-4" /> {actionLoading === 'convert' ? 'Converting...' : 'Convert to Invoice'}
          </button>
        )}
      </div>

      {/* ── Two Column: Line Items + Timeline ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Items (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card overflow-hidden">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Line Items</h2>
            <div className="overflow-x-auto -mx-4 sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2.5 font-medium">Description</th>
                    {isAccountant && <th className="px-4 py-2.5 font-medium">HSN/SAC</th>}
                    <th className="px-4 py-2.5 font-medium text-right">Qty</th>
                    <th className="px-4 py-2.5 font-medium text-right">Rate</th>
                    {isAccountant ? (
                      <>
                        <th className="px-4 py-2.5 font-medium text-right">GST %</th>
                        <th className="px-4 py-2.5 font-medium text-right">CGST</th>
                        <th className="px-4 py-2.5 font-medium text-right">SGST</th>
                        <th className="px-4 py-2.5 font-medium text-right">IGST</th>
                      </>
                    ) : (
                      <th className="px-4 py-2.5 font-medium text-right">Tax</th>
                    )}
                    <th className="px-4 py-2.5 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {est.lineItems?.map((li, i) => {
                    const lineTax = Number(li.cgst || 0) + Number(li.sgst || 0) + Number(li.igst || 0)
                    return (
                      <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">{li.description}</div>
                          {li.detailedDescription && (
                            <div className="text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{li.detailedDescription}</div>
                          )}
                        </td>
                        {isAccountant && <td className="px-4 py-2.5 text-gray-500">{li.hsnSacCode || '—'}</td>}
                        <td className="px-4 py-2.5 text-right text-gray-700">{Number(li.qty)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{formatCurrency(li.rate, est.currency)}</td>
                        {isAccountant ? (
                          <>
                            <td className="px-4 py-2.5 text-right text-gray-500">{Number(li.gstRate)}%</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.cgst || 0, est.currency)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.sgst || 0, est.currency)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.igst || 0, est.currency)}</td>
                          </>
                        ) : (
                          <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(lineTax, est.currency)}</td>
                        )}
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{formatCurrency(li.total, est.currency)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
              <div className="w-72 space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span>
                  <span className="text-gray-900">{formatCurrency(est.subtotal, est.currency)}</span>
                </div>
                {isAccountant ? (
                  <>
                    {Number(est.cgst) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>CGST</span>
                        <span>{formatCurrency(est.cgst, est.currency)}</span>
                      </div>
                    )}
                    {Number(est.sgst) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>SGST</span>
                        <span>{formatCurrency(est.sgst, est.currency)}</span>
                      </div>
                    )}
                    {Number(est.igst) > 0 && (
                      <div className="flex justify-between text-gray-500">
                        <span>IGST</span>
                        <span>{formatCurrency(est.igst, est.currency)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  totalTax > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <Tooltip text="Includes CGST, SGST, or IGST as applicable">
                        <span>Tax</span>
                      </Tooltip>
                      <span>{formatCurrency(totalTax, est.currency)}</span>
                    </div>
                  )
                )}
                <div className="flex justify-between font-semibold text-base border-t border-gray-200 pt-2 mt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">{formatCurrency(est.total, est.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {est.notes && (
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">Notes</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{est.notes}</p>
            </div>
          )}

          {/* Meta info (Accountant mode) */}
          {isAccountant && (
            <div className="card bg-gray-50 border-dashed">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Details</h2>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Currency</dt>
                  <dd className="font-medium text-gray-900">{est.currency}</dd>
                </div>
                {est.placeOfSupply && (
                  <div>
                    <dt className="text-gray-500">Place of Supply</dt>
                    <dd className="font-medium text-gray-900">{est.placeOfSupply}</dd>
                  </div>
                )}
                {est.createdBy && (
                  <div>
                    <dt className="text-gray-500">Created by</dt>
                    <dd className="font-medium text-gray-900 flex items-center gap-1.5">
                      <Avatar name={est.createdBy.name} size="sm" />
                      {est.createdBy.name}
                    </dd>
                  </div>
                )}
                {est.approvedBy && (
                  <div>
                    <dt className="text-gray-500">Approved by</dt>
                    <dd className="font-medium text-gray-900 flex items-center gap-1.5">
                      <Avatar name={est.approvedBy.name} size="sm" />
                      {est.approvedBy.name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        {/* Timeline (1/3 width) */}
        <div className="lg:col-span-1">
          <EstimateTimeline estimate={est} />
        </div>
      </div>

      {/* ── Confirmation Dialog ──────────────────────────────────── */}
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel}
        confirmClass={confirm.confirmClass}
        onConfirm={confirm.onConfirm}
        onCancel={closeConfirm}
      >
        {confirm.extra === 'declineReason' && (
          <div className="mt-3">
            <label className="label">Decline reason <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="input min-h-[80px]"
              value={declineReason}
              onChange={(e) => { setDeclineReason(e.target.value); declineReasonRef.current = e.target.value }}
              placeholder="Why was this estimate declined?"
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  )
}
